import { Alert, Badge, Card } from "react-bootstrap";
import { CalendarIcon, ClockIcon, DesktopComputerIcon, LocationMarkerIcon } from "@heroicons/react/outline";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { isString } from "lodash";
import { useMemo } from "react";

import CourseLinkButton from "./CourseLinkButton";
import CourseRegisterButton from "./CourseRegisterButton";
import RegistrationClosedMessage from "../components/RegistrationClosedMessage";

import { displayDate, getTimezone } from "../helpers/dates";
import { eventGuestsAllowed, isRemoteEvent, registrationIsOpen } from "../helpers/event";
import AddGuestButton from "./CoursesList/AddGuestButton";
import { useAuth } from "../services/wp.auth.service";
import { useCourseRegistration } from "../services/CourseRegistrationProvider";
import { utcToZonedTime } from "date-fns-tz";

const timeFormat = "h:mm a";

const MobileCourseCard = ({
	course,
	hideRegistrationClosedMessage,
	otherContent,
	showAddButton,
	showJoinButton
}) => {
	const { user } = useAuth();
	const { guests, registeredCourses, registeredFor } = useCourseRegistration();

	const inRegistrationList = useMemo(() => registeredFor(course), [registeredCourses]);
	const registered = useMemo(() => user && !!user?.registrations[course.id], [user]);
	const iconColorClass = useMemo(() => inRegistrationList ? "color-green" : "color-bluesky", [inRegistrationList]);

	const locationName = useMemo(() => isString(course.location) ? course.location : course?.location?.name || "", [course]);
	const allowsGuests = useMemo(() => eventGuestsAllowed(course), [course]);

	const isOnline = useMemo(() => isRemoteEvent(course), [course]);

	return (
		<Card className={classNames(
			"mb-4 shadow-sm p-1",
			{
				"border-0": !inRegistrationList,
				"border-green border-2 bg-green800": !!inRegistrationList,
				"bg-lighterblue": !!registered
			}
		)}
		>
			<Card.Body>
				<Card.Title
					as={Link}
					className="mb-3 text-neutral3 fw-bold d-block fs-20"
					to={`/class/${course.id}`}
				>{course.title}</Card.Title>

				<div className="mb-3 text-neutral5">
					<CalendarIcon className={`i me-2 ${iconColorClass}`} />
					{displayDate(utcToZonedTime(course.start, getTimezone()), "LLL do, yyy")}
					&nbsp;
					<Badge bg={classNames({
						info: !!registered,
						secondary: !inRegistrationList,
						success: inRegistrationList
					})}
					>EST</Badge>
				</div>
				<div className="mb-3 text-neutral5">
					<LocationMarkerIcon className={`i me-2 ${iconColorClass}`} />
					{locationName}
				</div>

				{isOnline
					? (
						<div className="mb-3 text-neutral5">
							<DesktopComputerIcon className={`i me-2 ${iconColorClass}`} /> Online with Video
						</div>
					) : null}
				<div className="mb-3 text-neutral5">
					<ClockIcon className={`i me-2 ${iconColorClass}`} />
					{/* eslint-disable-next-line max-len */}
					{displayDate(utcToZonedTime(course.start, getTimezone()), timeFormat)} - {displayDate(utcToZonedTime(course.end, getTimezone()), timeFormat)}
				</div>

				{allowsGuests && inRegistrationList ? (
					<div className="mb-3 text-neutral5">
						<AddGuestButton courseId={course.id} guests={guests[course.id]} />
					</div>
				) : null}

				{otherContent ? otherContent : null}

				{registered && showJoinButton ? <CourseLinkButton course={course} /> : null}

				{showAddButton ? (
					<>
						<hr className="mb-3" />

						{registrationIsOpen(course) || hideRegistrationClosedMessage
							? (
								<CourseRegisterButton
									className="text-start fw-bold p-0 text-neutral4"
									course={course}
									iconClassName={iconColorClass}
									variant="link"
								/>
							)
							: (
								<RegistrationClosedMessage
									className="text-center"
									course={course}
								/>
							)}
					</>
				) : null}

				{course.notes ? (
					<Alert className="m-0" variant="info">
						{course.notes}
					</Alert>
				) : null}
			</Card.Body>
		</Card>
	);
};

MobileCourseCard.propTypes = {
	course: PropTypes.shape({
		end: PropTypes.instanceOf(Date),
		id: PropTypes.number,
		location: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({
				name: PropTypes.string
			})
		]),
		notes: PropTypes.string,
		start: PropTypes.instanceOf(Date),
		title: PropTypes.string
	}).isRequired,
	hideRegistrationClosedMessage: PropTypes.bool,
	otherContent: PropTypes.node,
	showAddButton: PropTypes.bool,
	showJoinButton: PropTypes.bool
};

MobileCourseCard.defaultProps = {
	hideRegistrationClosedMessage: false,
	otherContent: null,
	showAddButton: true,
	showJoinButton: false
};

export default MobileCourseCard;
